import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import {useParams} from 'react-router-dom';
import appConfig from 'config/app.config';
import { getCookie, setCookie } from 'helpers/cookie-helper';
import Login from './login';
import Loading from 'components/loading/loading';
import {loginUiTexts } from 'data/ui-texts';
import SchoolController from 'components/users/school-controller';
import GroupController from 'components/users/group-controller';
import './login.scss';

const LoginController = ({scenarioId, updateBackground, handlePlayMusic}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isSchool, setIsSchool] = useState(false);
	const [userId, setUserId] = useState(null);
	const [schools, setSchools] = useState([]);

	const unsubscribeSchools = useRef(null);
	const unsubscribeOnAuthStateChanged = useRef(null);

	const {schoolId} = useParams();

	/**
	 * Subscribe to schools
	 */
	const subscribeToSchools = () => {
		if (unsubscribeSchools.current !== null) unsubscribeSchools.current();
		return new Promise((resolve, reject) => {
			const db = firebase.firestore();
			unsubscribeSchools.current = db.collection(appConfig.schoolsDbName).onSnapshot(
				(querySnapshot) => {
					let schools = [];
					querySnapshot.forEach((doc) => {
						schools.push({id: doc.id, ...doc.data()});
					});
					setSchools(schools);
					resolve({status: 'success', schools});
				},
				(error) => {
					console.error(error);
					resolve({status: 'error', error});
				}
			);
		});
	};	


	/**
	 * Check if user is logged in
	 */
	const checkIfLoggedIn = (schools) => {
		// Unsubscribe previous onAuthStateChanged
		if (unsubscribeOnAuthStateChanged.current !== null) {
			unsubscribeOnAuthStateChanged.current();
		}

		// Subscribe to onAuthStateChanged
		unsubscribeOnAuthStateChanged.current = firebase.auth().onAuthStateChanged((user)=>{
			if (user) {
				if (user.email) {
					/* Logged in as admin */
					handleLogout();
				} else {
					/* Ok to cookies */
					const cookieConsent = getCookie(appConfig.cookiesAcceptedCookieName);
					if (!cookieConsent) {
						setCookie(appConfig.cookiesAcceptedCookieName, 'ok');
					}

					const isSchool = schools.some((s) => {return s.id === user.uid;});
					if (isSchool) {
					/* Logged in as school */
						setIsLoggedIn(true); 
						setIsLoading(false); 
						setUserId(user.uid); 
						setIsSchool(true);
					} else {
					/* Logged in as group */
						setIsLoggedIn(true); 
						setIsLoading(false); 
						setUserId(user.uid); 
						setIsSchool(false);
					}
				}
			} else {
				/* Not logged in */
				setIsLoading(false);
				setIsLoggedIn(false);
				setUserId(null);
				setIsSchool(false);
			}
		});	
	};

	/**
	 * Logout
	 */
	const handleLogout = () => {
		firebase.auth().signOut();
		setIsLoading(false);
		setIsLoggedIn(false);
		setUserId(null);
		setIsSchool(false);
		updateBackground('login', scenarioId);
	};



	useEffect(() => {
		/* Subscribe to schools */
		subscribeToSchools().then((response) => {
			/* Check if logged in */
			if (response.status === 'success') {
				checkIfLoggedIn(response.schools);
			}
			updateBackground('login', scenarioId);
		}).catch((error) => {
			console.error(error);
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	/* Loading */
	if (isLoading) {
		return (
			<Loading
				loadErrMsg={loginUiTexts.loadingGame} 
				handleLogout={handleLogout}
			/>
		);
	}
				
	/* User is not loggged in */
	if (!isLoggedIn) {
		return (
			<Login scenarioId={scenarioId} schoolId={schoolId} schools={schools} />
		);
	}

	/* User is logged in, get controller component for school / group */
	let UserComponent = (isSchool ? SchoolController : GroupController);
	return (
		<UserComponent 
			scenarioId={scenarioId}
			userId={userId}
			schoolId={schoolId}
			schools={schools}
			handleLogout={handleLogout}
			updateBackground={updateBackground}
			handlePlayMusic={handlePlayMusic}
		/>
	);
};


LoginController.propTypes = {
	scenarioId: PropTypes.string.isRequired,
	updateBackground: PropTypes.func.isRequired,
	handlePlayMusic: PropTypes.func.isRequired,
};

export default LoginController;